import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chips,
  Choice,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Panel,
  Select,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
} from '@dayon/design-system';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession, type Brand } from '../../lib/session.tsx';
import { allBrandsPhrase } from '../../lib/brand-words.ts';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { downloadRun } from './download.ts';
import { describeCron, formatBytes, formatCell, formatMoment, daysAgo, today } from './format.ts';
import {
  builderWindowLine,
  cutShortAdvice,
  emptyRunWords,
  historyWindow,
  windowLine,
} from './run-words.ts';
import type {
  CatalogueResponse,
  ReportRow,
  RunHistoryRow,
  RunResult,
  ScheduleRow,
  SourcesResponse,
} from './types.ts';

/**
 * Reports & analytics — §17.
 *
 * The catalogue, the run history and the schedules are all READ FROM THE
 * DATABASE. `reporting.report_definition` holds the eight standing reports (see
 * packages/db/sql/060_report_catalogue.sql), `report_run` records every
 * execution and `report_artefact` stores the file it produced.
 *
 * WHAT THIS SCREEN IS CAREFUL ABOUT.
 *
 * · Every read has its OWN loading and failure state, and a failed read clears
 *   its rows. A stale table sitting under an error notice is how somebody reads
 *   last week's numbers as today's.
 * · A run's result is shown from the run, and the download comes from the
 *   STORED artefact rather than a re-execution — two people downloading "the
 *   January extract" a week apart must get the same bytes.
 * · A schedule's brand scope is a DECISION, not a leftover view filter. It is
 *   picked in the form and posted explicitly — an id, or null for every brand —
 *   because no one is signed in at 06:00 when the worker fires it, and the row
 *   it names is the only thing that limits what the report reaches.
 * · Nothing claims a separate warehouse. Five of the eight reports read
 *   transactional tables directly, because a reconciliation has to carry
 *   individual rows. What actually protects the platform is the read-only role,
 *   the row cap and the statement timeout, and the notice says exactly that.
 */

type CategoryFilter = 'All' | string;

const RANGES = [
  { key: '7', label: 'Last 7 days' },
  { key: '30', label: 'Last 30 days' },
  { key: '90', label: 'Last 90 days' },
  { key: 'custom', label: 'Custom…' },
] as const;

/* ── Catalogue columns ──────────────────────────────────────────────────────
   Cadence and Last run are `secondary` so they drop out on a phone; the report
   name, what it reads and the run button survive at every width. */
function reportColumns(
  onRun: (r: ReportRow) => void,
  running: string | null,
): Column<ReportRow>[] {
  return [
    {
      key: 'report',
      header: 'Report',
      render: (r) => (
        <>
          {r.name}
          {/*
            THE DESCRIPTION WAS ALWAYS HERE AND WAS NEVER DRAWN.

            `reporting.report_definition.description` is selected by the route
            and arrives on every row of this table. Nine reports carry one, each
            written where the report was defined and each saying the thing an
            operator needs before they run it — that deposit reconciliation
            reads the payment table directly because a rollup cannot be
            reconciled against, that a statement is not evidence of payment,
            that the self-exclusion date range is when an exclusion was PLACED
            rather than who is excluded today.

            All of it sat in the response, unrendered, while this screen was the
            thinnest in the console: one notice in 1,403 lines. Somebody chose
            which compliance extract to send a regulator from a name and a
            category.

            AND THE LINE IT REPLACES WAS A TABLE NAME. This row's second line
            used to be `r.reads` — `agent.agent_period_result`,
            `player.player_restriction`, `game.game` — printed under every
            report. An operator can do nothing with a table name: it is not
            something they can look up, ask for, or repeat to anybody. The
            description states the same provenance in words where it matters
            ("from the nightly rollup", "reads the payment table directly
            because a rollup cannot be reconciled against"), which is the
            version that was always meant to be read.

            `no-schema-names-on-screen.test.ts` could not see it, and that is
            worth knowing: the rule scans string literals in source, and this
            name arrived from the API at run time. The route still sends
            `reads`; nothing renders it.
          */}
          <div className="cell-sub">{r.description}</div>
        </>
      ),
    },
    { key: 'category', header: 'Category', render: (r) => <Tag tone="neutral">{r.category}</Tag> },
    {
      key: 'lastRun',
      header: 'Last run',
      secondary: true,
      render: (r) =>
        r.lastRun ? (
          <>
            <span>{formatMoment(r.lastRun.at)}</span>
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {r.lastRun.status === 'SUCCEEDED'
                ? `${r.lastRun.rowCount ?? 0} rows · ${r.lastRun.ref}`
                : `${r.lastRun.status.toLowerCase()} · ${r.lastRun.ref}`}
            </div>
          </>
        ) : (
          /* Never run is a fact about this deployment, not a missing feature —
             so it reads as a state rather than as an apology. */
          <span className="text-subtle">never run</span>
        ),
    },
    {
      key: 'schedule',
      header: 'Schedule',
      secondary: true,
      render: (r) =>
        r.schedule ? (
          <Tag tone="accent">{describeCron(r.schedule.cron)}</Tag>
        ) : (
          <span className="text-subtle">not scheduled · {r.cadence.toLowerCase()} intended</span>
        ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (r) => (
        <Button small onClick={() => onRun(r)} disabled={running !== null} aria-label={`Run ${r.name} now`}>
          {running === r.key ? 'Running…' : 'Run now'}
        </Button>
      ),
    },
  ];
}

export function Reports() {
  const { brandId, brands, allowed, session } = useSession();
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [catalogue, setCatalogue] = useState<CatalogueResponse | null>(null);
  const [loadingCatalogue, setLoadingCatalogue] = useState(true);
  const [filter, setFilter] = useState<CategoryFilter>('All');

  const [rangeKey, setRangeKey] = useState<string>('30');
  const [from, setFrom] = useState(() => daysAgo(30));
  const [to, setTo] = useState(() => today());

  const [running, setRunning] = useState<string | null>(null);
  const [result, setResult] = useState<{ report: string; run: RunResult } | null>(null);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [schedulesOpen, setSchedulesOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);

  const canSchedule = allowed('reports.schedule');

  /** The window every run uses. Custom fields drive it directly. */
  const window = useMemo(() => {
    if (rangeKey === 'custom') return { from, to };
    const n = Number(rangeKey);
    return { from: daysAgo(n), to: today() };
  }, [rangeKey, from, to]);

  const loadCatalogue = useCallback(() => {
    setLoadingCatalogue(true);
    api
      .get<CatalogueResponse>('/api/reports')
      .then(setCatalogue)
      .catch((error: unknown) => {
        // The rows are cleared, not left stale: a catalogue that failed to
        // refresh must not sit under an error dialog looking current.
        setCatalogue(null);
        showError({
          title: 'Could not load the report catalogue',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The catalogue could not be read. Nothing was run.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => setLoadingCatalogue(false));
  }, [showError]);

  useEffect(loadCatalogue, [loadCatalogue]);

  const runReport = useCallback(
    (report: ReportRow) => {
      setRunning(report.key);
      api
        .post<RunResult>(`/api/reports/${report.key}/run`, {
          ...window,
          ...(brandId ? { brandId } : {}),
        })
        .then((run) => {
          setResult({ report: report.name, run });
          // The catalogue's Last run column is now out of date by exactly this
          // run, so it is refreshed rather than patched in place.
          loadCatalogue();
        })
        .catch((error: unknown) => {
          showError({
            title: `Could not run ${report.name}`,
            message:
              error instanceof ApiRequestError
                ? error.message
                : 'The report did not run. Nothing was exported.',
            ...(error instanceof ApiRequestError ? { code: error.code } : {}),
          });
        })
        .finally(() => setRunning(null));
    },
    [window, brandId, loadCatalogue, showError],
  );

  const columns = useMemo(() => reportColumns(runReport, running), [runReport, running]);

  const categories = useMemo(() => {
    if (!catalogue) return [];
    // Computed from the rows, never written down: a chip promising four rows
    // and filtering to three is a discrepancy an operator raises as a bug.
    const seen = new Map<string, number>();
    for (const r of catalogue.reports) seen.set(r.category, (seen.get(r.category) ?? 0) + 1);
    return [...seen.entries()].toSorted((a, b) => a[0].localeCompare(b[0]));
  }, [catalogue]);

  const chipOptions = useMemo(
    () => [
      { key: 'All' as CategoryFilter, label: 'All', count: catalogue?.reports.length ?? 0 },
      ...categories.map(([c, n]) => ({ key: c as CategoryFilter, label: c, count: n })),
    ],
    [catalogue, categories],
  );

  const visible = useMemo(() => {
    const rows = catalogue?.reports ?? [];
    return filter === 'All' ? rows : rows.filter((r) => r.category === filter);
  }, [catalogue, filter]);

  /* An admin held to one brand read "scoped to all brands" (module walk, 30 Oct 2026). */
  const brandLabel = brandId
    ? (brands.find((b) => b.id === brandId)?.name ?? brandId)
    : allBrandsPhrase(brands);

  if (!allowed('audit.export')) {
    return (
      <EmptyState icon="lock-key" title="Reports & analytics">
        This module needs the audit.export capability. Your role does not hold it, and report
        scope always respects the requesting admin&rsquo;s permissions.
      </EmptyState>
    );
  }

  return (
    <>
      {/*
        THE SENTENCE UNDER THE NAME IS WHAT THIS HEADER IS FOR.

        This screen opened with a name, a count and three buttons, above a
        notice about a read-only role and a row cap. All of it was true and none
        of it said what the screen was FOR, which on this module is the sharpest
        version of the problem in the console: nobody comes here daily. The
        people who arrive are a finance clerk at month end and whoever has been
        handed a regulator's request, each of them sent once, each needing to
        know before they touch anything that the thing to do is pick a report,
        set a date range, and take away a file.

        The explanation was already written — at the top of this file, in a
        module comment, where only somebody editing the source would ever find
        it. That has been true of twenty screens, and it is why `purpose` is a
        required prop rather than an optional one.
      */}
      <ScreenHeader
        /*
          "Reports & analytics", not the sidebar's shorter "Reports".

          The sidebar label is a nav-width abbreviation of this name, but the
          product's own module list, the operator manual's section heading and
          the capability refusal a few lines above all call the screen "Reports
          & analytics" — and the manual is the thing that sends somebody here
          looking for a heading to match. Shortening the heading to fit the
          sidebar would leave three places pointing at a name the screen no
          longer shows, which is the failure `one-name-for-one-screen` exists
          to catch. One screen, one name, whichever door reaches it.
        */
        title="Reports & analytics"
        purpose="Run the standing reports over a date range and download the file — for finance reconciling the books, or a regulator asking for figures."
        /*
          The caption moved here unchanged — both halves, and the same two
          states it has always had.

          The brand scope and "your permissions" trail every state because they
          are true of all of them, and on this screen they are not decoration:
          two admins running the same report do not necessarily get the same
          rows, so a count of reports means nothing to a reader who does not
          know whose view produced it. The notice below says the same thing at
          length; this says it in the line where the figure is.

          NOTE FOR WHOEVER EXTENDS THIS, because it is a live rough edge rather
          than a tidy omission: this caption is TWO-state, and the second state
          is doing double duty. `catalogue` is null both before the first read
          lands and after a read FAILS — the catch clears the rows deliberately,
          so a stale table never sits under an error dialog looking current —
          which means a failed read leaves this line reading "Loading catalogue"
          while the panel underneath correctly says "The catalogue could not be
          read". The panel is where this file draws the "genuinely nothing"
          versus "we could not ask" line, and it draws it properly. The caption
          was carried across exactly as it was rather than quietly grown a third
          state in a commit about headers, but the two surfaces do currently
          disagree, and the fix is a real one: a separate `failed` flag set in
          the catch, so this line can say the read failed instead of claiming a
          load that is no longer running.
        */
        meta={
          <>
            {catalogue ? `${catalogue.reports.length} standing reports` : 'Loading catalogue'} ·
            scoped to {brandLabel} and your permissions
          </>
        }
        actions={
          <>
            <Button onClick={() => setHistoryOpen(true)}>
              <Icon name="clock-counter-clockwise" />
              Run history
            </Button>
            <Button onClick={() => setSchedulesOpen(true)}>
              <Icon name="calendar-blank" />
              Schedules
            </Button>
            <Button variant="primary" onClick={() => setBuilderOpen(true)}>
              <Icon name="sliders" />
              Build report
            </Button>
          </>
        }
      />

      {/* What is actually true. The screen used to claim a "warehouse copy" that
          a heavy export never touches — there is no separate warehouse, it is
          the same Postgres, and five of the eight reports read transactional
          tables directly because a reconciliation has to carry individual rows.
          These three limits are the real protection, so these are what it says. */}
      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          Reports run with <strong>read-only access</strong> that cannot reach credentials
          or write anything, capped at {catalogue?.rowCap.toLocaleString() ?? '5,000'} rows with a
          20-second timeout. Scope always respects the brand filter and your permissions — two
          admins running the same report do not necessarily get the same rows.
        </div>
      </div>

      <Panel
        title="Standing reports"
        actions={
          <div className="row">
            <Select
              value={rangeKey}
              onChange={(e) => setRangeKey(e.target.value)}
              aria-label="Reporting window"
            >
              {RANGES.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </Select>
            {rangeKey === 'custom' ? (
              <>
                <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
                <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} aria-label="To" />
              </>
            ) : null}
          </div>
        }
      >
        {/* A snapshot below — Games hosted — is not said to cover these dates: they name its
            file and filter nothing. */}
        <div className="text-subtle" style={{ fontSize: '12px', marginBottom: 'var(--space-3)' }}>
          {windowLine(
            window,
            visible.filter((r) => r.snapshot).map((r) => r.name),
            visible.length,
          )}
        </div>
        <Chips options={chipOptions} active={filter} onChange={setFilter} label="Filter reports by category" />
        {loadingCatalogue && !catalogue ? (
          <div className="skeleton" style={{ height: '220px', marginTop: 'var(--space-4)' }}>&nbsp;</div>
        ) : catalogue === null ? (
          <EmptyState icon="warning-circle" title="The catalogue could not be read">
            The error dialog carries the reason. Nothing was run, and no export was produced.
            <br />
            <Button small onClick={loadCatalogue} style={{ marginTop: 'var(--space-3)' }}>
              Try again
            </Button>
          </EmptyState>
        ) : (
          <DataTable rows={visible} columns={columns} rowKey={(r) => r.key} caption="Standing reports" />
        )}
      </Panel>

      <RunResultDialog
        result={result}
        onClose={() => setResult(null)}
        onDownload={(ref, format) => void downloadRun(ref, showError, toast, format)}
      />
      <RunHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        /* The history list offers the CSV only: it is a list of past runs, and a
           format picker on every row would be four controls where one is needed.
           The PDF is offered where a run is being LOOKED at. */
        onDownload={(ref: string) => void downloadRun(ref, showError, toast, 'CSV')}
      />
      <SchedulesDialog
        open={schedulesOpen}
        onClose={() => setSchedulesOpen(false)}
        canSchedule={canSchedule}
        reports={catalogue?.reports ?? []}
        brands={brands}
        consoleBrandId={brandId}
        /* The scope restriction, not the brand list: an admin with every brand has none. */
        limited={(session?.brands.length ?? 0) > 0}
        onChanged={loadCatalogue}
      />
      <BuilderDialog
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        window={window}
        brandId={brandId}
        onRan={(run) => {
          setBuilderOpen(false);
          setResult({ report: 'Ad-hoc report', run });
        }}
      />
    </>
  );
}

/* ── The result of a run ────────────────────────────────────────────────────
   A preview plus the download. The preview is capped at 50 rows because a
   5,000-row table in a modal is not readable and is not the point — the file
   is the deliverable. */
const PREVIEW_ROWS = 50;

export function RunResultDialog({
  result,
  onClose,
  onDownload,
}: {
  result: { report: string; run: RunResult } | null;
  onClose: () => void;
  /* The format is the caller's — see the two buttons below, and item 34. */
  onDownload: (ref: string, format: 'CSV' | 'PDF') => void;
}) {
  if (!result) return null;
  const { run } = result;
  const preview = run.rows.slice(0, PREVIEW_ROWS);
  const empty = emptyRunWords(run);

  return (
    <Dialog
      open
      onClose={onClose}
      wide
      title={`${result.report} · ${run.ref}`}
      actions={
        <>
          <Button onClick={onClose}>Close</Button>
          {/*
            * TWO FORMATS, BOTH OF THE SAME EXTRACT. Item 34 lets the regulator name
            * the format; the PDF is rendered from the stored CSV rather than from a
            * second query, so the two cannot disagree about what the run contained.
            *
            * CSV stays the primary button: it is what a finance team reconciles
            * against and what every schedule mails. The PDF is what goes in a folder.
            *
            * BOTH STAY ENABLED ON A RUN THAT MATCHED NOTHING. Every run stores a file,
            * and PAGCOR item 34(b) wants a report "even if the period specified
            * contains no data": the CSV carries the column headings and the PDF says in
            * words that there were no rows. Both were disabled here, so the PDF of an
            * empty period could be reached from no screen at all.
            */}
          <Button variant="secondary" onClick={() => onDownload(run.ref, 'PDF')}>
            <Icon name="file-pdf" />
            Download PDF
          </Button>
          <Button variant="primary" onClick={() => onDownload(run.ref, 'CSV')}>
            <Icon name="download-simple" />
            Download CSV
          </Button>
        </>
      }
    >
      <div className="text-subtle" style={{ fontSize: '12.5px', marginBottom: 'var(--space-3)' }}>
        {run.rowCount.toLocaleString()} row{run.rowCount === 1 ? '' : 's'} in {run.durationMs} ms.
        {run.rows.length > PREVIEW_ROWS
          ? ` Showing the first ${PREVIEW_ROWS}; the download carries all of them.`
          : ''}
      </div>

      {run.truncated ? (
        /* The cap firing means the answer is INCOMPLETE, and saying so is the
           whole reason the runner returns the flag. A truncated export that
           looks complete is a reconciliation that silently balances. */
        <div className="notice">
          <Icon name="warning-circle" />
          <div>
            <strong>This result was cut short by the row cap.</strong> It is the first{' '}
            {run.rowCount.toLocaleString()} rows, not the whole answer. {cutShortAdvice(run.snapshot)}
          </div>
        </div>
      ) : null}

      {/* The file of an empty run exists and is offered above; this says what is in it. */}
      {run.rowCount === 0 ? (
        <EmptyState icon="magnifying-glass" title={empty.title}>
          {empty.body}
        </EmptyState>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                {run.columns.map((c) => (
                  <th key={c.key} scope="col">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.map((row, i) => (
                <tr key={i}>
                  {run.columns.map((c) => (
                    <td key={c.key} style={c.kind === 'text' ? undefined : { textAlign: 'right' }}>
                      {formatCell(row[c.key] ?? null, c.kind)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}

/* ── Run history ────────────────────────────────────────────────────────── */
function RunHistoryDialog({
  open,
  onClose,
  onDownload,
}: {
  open: boolean;
  onClose: () => void;
  onDownload: (ref: string) => void;
}) {
  const { showError } = useErrorDialog();
  const [runs, setRuns] = useState<RunHistoryRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get<{ runs: RunHistoryRow[] }>('/api/reports/runs?limit=50')
      .then((d) => setRuns(d.runs))
      .catch((error: unknown) => {
        setRuns(null);
        showError({
          title: 'Could not read the run history',
          message: error instanceof ApiRequestError ? error.message : 'The history could not be read.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => setLoading(false));
  }, [open, showError]);

  const columns: Column<RunHistoryRow>[] = [
    {
      key: 'run',
      header: 'Run',
      render: (r) => (
        <>
          {r.name}
          {/* A snapshot's row says when it was taken; a dated one, the Manila dates its file
              is named by. */}
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {r.ref} · {historyWindow(r)}
          </div>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Result',
      render: (r) =>
        r.status === 'SUCCEEDED' ? (
          /*
           * A CUT RUN IS MARKED IN THE HISTORY, not only while it is fresh.
           *
           * The panel above says so for a run the operator has just started; this
           * is the list they come back to a week later to find the file they
           * already made. Without the flag here, "5,000 rows" reads as the answer
           * — and on real volumes a monthly extract hits that cap routinely.
           */
          <>
            <Tag tone={r.truncated ? 'outline' : 'accent'}>
              {(r.rowCount ?? 0).toLocaleString()} rows
            </Tag>
            {r.truncated ? (
              <div className="text-subtle" style={{ fontSize: '11px' }}>
                cut at the row limit — not the whole answer
              </div>
            ) : null}
          </>
        ) : r.status === 'RUNNING' ? (
          <Tag tone="quiet">running</Tag>
        ) : (
          /* The reason, on the row. A failure the operator has to open a log to
             understand is a failure they will simply retry. */
          <>
            <Tag tone="outline">failed</Tag>
            <div className="text-subtle" style={{ fontSize: '11px' }}>{r.error}</div>
          </>
        ),
    },
    { key: 'who', header: 'Requested by', secondary: true, render: (r) => r.requestedBy },
    { key: 'when', header: 'Started', secondary: true, render: (r) => formatMoment(r.startedAt) },
    {
      key: 'file',
      header: 'File',
      render: (r) =>
        r.download ? (
          <Button small onClick={() => onDownload(r.ref)} aria-label={`Download ${r.ref}`}>
            CSV · {formatBytes(r.download.byteSize)}
          </Button>
        ) : (
          <span className="text-subtle">—</span>
        ),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} wide title="Run history" actions={<Button onClick={onClose}>Close</Button>}>
      {loading && !runs ? (
        <div className="skeleton" style={{ height: '180px' }}>&nbsp;</div>
      ) : runs === null ? (
        <EmptyState icon="warning-circle" title="History unavailable">
          The error dialog carries the reason.
        </EmptyState>
      ) : runs.length === 0 ? (
        <EmptyState icon="clock-counter-clockwise" title="Nothing has been run yet">
          Every run — by hand or by schedule — is recorded here with who asked, what window it
          covered and the file it produced.
        </EmptyState>
      ) : (
        <DataTable rows={runs} columns={columns} rowKey={(r) => r.ref} caption="Report runs" />
      )}
    </Dialog>
  );
}

/* ── Schedules ──────────────────────────────────────────────────────────── */
/**
 * The value the scope control carries for "every brand".
 *
 * A sentinel rather than '' or undefined, because those read as "the form did
 * not ask" — and the two must stay distinguishable all the way to the wire.
 * `reporting.report_schedule.brand_id` is nullable and the route accepts null;
 * every brand is therefore a real answer that is SENT, not an absent key.
 */
const ALL_BRANDS = '__all_brands__';

const CRON_PRESETS = [
  { cron: '0 6 * * *', label: 'Daily at 06:00' },
  { cron: '0 7 * * *', label: 'Daily at 07:00' },
  { cron: '0 */6 * * *', label: 'Every 6 hours' },
  { cron: '0 6 * * 1', label: 'Mondays at 06:00' },
  { cron: '0 6 1 * *', label: 'First of the month, 06:00' },
];

export function SchedulesDialog({
  open,
  onClose,
  canSchedule,
  reports,
  brands,
  consoleBrandId,
  limited,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  canSchedule: boolean;
  reports: ReportRow[];
  /**
   * Every brand this admin may SEE — /api/brands is already filtered by their
   * scope. The route answers a brand outside that scope with a 404, so offering
   * one here would be offering a choice the server refuses.
   */
  brands: Brand[];
  /** The console's global brand filter. It SEEDS the control, nothing more. */
  consoleBrandId: string | null;
  /** True when this admin's access is limited to some brands. */
  limited: boolean;
  onChanged: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [rows, setRows] = useState<ScheduleRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [definitionKey, setDefinitionKey] = useState('');
  const [cron, setCron] = useState(CRON_PRESETS[0]!.cron);
  /* Typed as one line, because that is how an operator has the list — pasted
     out of an email. Split on submit, not on every keystroke: splitting as they
     type makes a half-typed address into an entry and then complains about it. */
  const [recipientsText, setRecipientsText] = useState('');
  const [recipientsError, setRecipientsError] = useState<string | null>(null);
  /* null is "the operator has not touched the control", NOT "no scope": the
     select then shows `seededScope`, which is always a real, visible choice. */
  const [scopeChoice, setScopeChoice] = useState<string | null>(null);

  /*
   * WHY THE GLOBAL BRAND FILTER ONLY *SEEDS* THIS CONTROL.
   *
   * A schedule fires unattended at 06:00 for as long as it exists, so its reach
   * is not a view setting — it is part of what was created. Reading the header's
   * brand filter at submit time and posting it silently would mean whoever
   * happened to have "FairPlay" selected creates a FairPlay-only schedule they
   * never chose, and reads "All brands" nowhere on screen to tell them
   * otherwise. So the scope is a control the operator can see and change, and
   * the filter only decides what it says when the dialog opens — which is the
   * scope they are already working in, and therefore the honest default.
   *
   * The seed is checked against the brands actually in scope: a brand id left
   * in localStorage from another session, or one since deactivated, falls back
   * to all brands — or, for a brand-limited admin, to their first brand —
   * rather than pre-selecting an option that is not on the list.
   */
  /*
   * A BRAND-LIMITED ADMIN IS NOT OFFERED EVERY BRAND. A schedule naming no brand fires over every
   * brand with nobody signed in, so the platform refuses one to an admin whose access is limited
   * to some brands. Their control starts on one of their brands — the header's, or the first —
   * and offers no "All brands".
   */
  const seededScope = useMemo(() => {
    if (consoleBrandId && brands.some((b) => b.id === consoleBrandId)) return consoleBrandId;
    return limited ? (brands[0]?.id ?? ALL_BRANDS) : ALL_BRANDS;
  }, [consoleBrandId, brands, limited]);
  const scope = scopeChoice ?? seededScope;
  const scopeName = brands.find((b) => b.id === scope)?.name ?? 'all brands';

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<{ schedules: ScheduleRow[] }>('/api/reports/schedules')
      .then((d) => setRows(d.schedules))
      .catch((error: unknown) => {
        setRows(null);
        showError({
          title: 'Could not read the schedules',
          message: error instanceof ApiRequestError ? error.message : 'The schedules could not be read.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => setLoading(false));
  }, [showError]);

  useEffect(() => {
    // Closing forgets the choice so the next open re-seeds from the filter the
    // operator is looking at then, rather than one from ten minutes ago.
    if (open) load();
    else {
      setScopeChoice(null);
      setRecipientsText('');
      setRecipientsError(null);
    }
  }, [open, load]);

  const create = () => {
    /* Split on comma, semicolon, newline or space — every separator a pasted
       list actually arrives with. Validated HERE as well as at the route, so a
       typo is answered beside the field rather than as a modal over the whole
       dialog; the route's own check is the one that decides. */
    const recipients = [
      ...new Set(
        recipientsText
          .split(/[\s,;]+/)
          .map((a) => a.trim().toLowerCase())
          .filter(Boolean),
      ),
    ];
    const bad = recipients.filter((a) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a));
    if (bad.length > 0) {
      setRecipientsError(
        `${bad.join(', ')} ${bad.length === 1 ? 'is not an email address' : 'are not email addresses'}.`,
      );
      return;
    }
    if (recipients.length > 10) {
      setRecipientsError('A schedule may name at most 10 recipients.');
      return;
    }
    setRecipientsError(null);

    setBusy(true);
    api
      .post('/api/reports/schedules', {
        definitionKey,
        cron,
        /* Always sent, null included. `...(scope ? { brandId } : {})` would make
           "every brand" indistinguishable from a form that never asked — and
           this body is what the audit row records the schedule as. */
        brandId: scope === ALL_BRANDS ? null : scope,
        recipients,
      })
      .then(() => {
        /* The scope is named back, and so is what happens to the file. Neither
           is asserted: an empty list means nobody is mailed, and the sentence
           says that rather than implying a delivery nobody arranged. */
        toast(
          `Schedule created for ${scopeName}. The platform picks it up within five minutes. ` +
            (recipients.length === 0
              ? 'No one is mailed — download the file from Run history.'
              : `The CSV is then recorded for ${recipients.length === 1 ? recipients[0] : `${recipients.length} recipients`}.`),
        );
        // The report is cleared and the scope is NOT: setting up several reports
        // for one brand in a sitting is the common case, and the control stays
        // on screen saying which brand that is. The recipients go with the
        // report, not with the brand, so they are cleared too.
        setDefinitionKey('');
        setRecipientsText('');
        load();
        onChanged();
      })
      .catch((error: unknown) => {
        showError({
          title: 'Could not create the schedule',
          message: error instanceof ApiRequestError ? error.message : 'The schedule was not created.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => setBusy(false));
  };

  const togglePause = (s: ScheduleRow) => {
    setBusy(true);
    api
      .post<{ active: boolean }>(`/api/reports/schedules/${s.id}/pause`)
      .then((d) => {
        toast(`${s.definitionName} ${d.active ? 'resumed' : 'paused'}.`);
        load();
        onChanged();
      })
      .catch((error: unknown) => {
        showError({
          title: 'Could not change the schedule',
          message: error instanceof ApiRequestError ? error.message : 'Nothing was changed.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => setBusy(false));
  };

  const columns: Column<ScheduleRow>[] = [
    {
      key: 'report',
      header: 'Report',
      render: (s) => (
        <>
          {s.definitionName}
          <div className="text-subtle" style={{ fontSize: '11px' }}>{s.brand}</div>
        </>
      ),
    },
    {
      /* WHO GETS THE FILE, on the row rather than behind an edit dialog. A
         standing schedule mails platform figures to somebody outside this
         console every morning, and "why is finance getting this?" has to be
         answerable by looking. */
      key: 'recipients',
      header: 'Emailed to',
      secondary: true,
      render: (s) =>
        s.recipients.length === 0 ? (
          <span className="text-subtle">nobody · download from Run history</span>
        ) : (
          <>
            {s.recipients[0]}
            {s.recipients.length > 1 ? (
              <div className="text-subtle" style={{ fontSize: '11px' }}>
                and {s.recipients.length - 1} more
              </div>
            ) : null}
          </>
        ),
    },
    {
      key: 'cadence',
      header: 'Cadence',
      render: (s) => (
        <>
          {describeCron(s.cron)}
          <div className="text-subtle" style={{ fontSize: '11px' }}>{s.cron} · {s.timezone}</div>
        </>
      ),
    },
    {
      key: 'lastFired',
      header: 'Last fired',
      secondary: true,
      render: (s) =>
        s.lastFiredAt ? formatMoment(s.lastFiredAt) : <span className="text-subtle">not yet</span>,
    },
    {
      key: 'state',
      header: 'State',
      render: (s) =>
        s.active ? <Tag tone="accent">active</Tag> : <Tag tone="quiet">paused</Tag>,
    },
    ...(canSchedule
      ? [
          {
            key: 'action',
            header: 'Action',
            render: (s: ScheduleRow) => (
              <Button small onClick={() => togglePause(s)} disabled={busy}>
                {s.active ? 'Pause' : 'Resume'}
              </Button>
            ),
          } satisfies Column<ScheduleRow>,
        ]
      : []),
  ];

  return (
    <Dialog open={open} onClose={onClose} wide title="Report schedules" actions={<Button onClick={onClose}>Close</Button>}>
      {loading && !rows ? (
        <div className="skeleton" style={{ height: '140px' }}>&nbsp;</div>
      ) : rows === null ? (
        <EmptyState icon="warning-circle" title="Schedules unavailable">
          The error dialog carries the reason.
        </EmptyState>
      ) : rows.length === 0 ? (
        <EmptyState icon="calendar-blank" title="No report is scheduled">
          {canSchedule
            ? 'Nothing runs unattended today. Add a schedule below and the platform will run it.'
            : 'Nothing runs unattended today. Creating a schedule needs the reports.schedule capability, which your role does not hold.'}
        </EmptyState>
      ) : (
        <DataTable rows={rows} columns={columns} rowKey={(s) => s.id} caption="Report schedules" />
      )}

      {canSchedule ? (
        <div style={{ marginTop: 'var(--space-6)', borderTop: '1px solid var(--color-line)', paddingTop: 'var(--space-6)' }}>
          <div className="text-subtle" style={{ fontSize: '12px', marginBottom: 'var(--space-3)' }}>
            A schedule runs unattended over the brand it names, or across every brand when it names
            none. Nobody is signed in when it fires, so the scope below is the only thing that
            limits it.
          </div>
          <Field label="Schedule a report">
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={definitionKey}
                onChange={(e) => setDefinitionKey(e.target.value)}
              >
                <option value="">Choose a report…</option>
                {reports.map((r) => (
                  <option key={r.key} value={r.key}>{r.name}</option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label="Brand scope"
            hint={
              limited
                ? 'Your access is limited to some brands, so a schedule runs for one of them.'
                : brands.length === 0
                  ? 'No brand is listed for your account, so this schedule can only run across all brands.'
                  : 'Fixed here, not read from the header filter when it fires. Change it and the next schedule you create carries the new scope.'
            }
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={scope}
                onChange={(e) => setScopeChoice(e.target.value)}
              >
                {limited ? null : <option value={ALL_BRANDS}>All brands</option>}
                {/* Only the brands the route would accept from this admin. */}
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label="Cadence"
            hint="Read in Asia/Manila, the platform's own gaming day — not the server's timezone."
          >
            {({ id, describedBy }) => (
              <Select id={id} aria-describedby={describedBy} value={cron} onChange={(e) => setCron(e.target.value)}>
                {CRON_PRESETS.map((p) => (
                  <option key={p.cron} value={p.cron}>{p.label}</option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label="Email the CSV to"
            {...(recipientsError ? { error: recipientsError } : {})}
            hint="Up to 10 addresses, separated by commas. Leave empty and nobody is mailed — the run still happens and the file is in Run history."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                type="text"
                placeholder="finance@dayon.ph, ops@dayon.ph"
                value={recipientsText}
                onChange={(e) => {
                  setRecipientsText(e.target.value);
                  setRecipientsError(null);
                }}
              />
            )}
          </Field>
          <Button
            variant="primary"
            onClick={create}
            /* A brand-limited admin with no brand listed has nothing the platform would accept. */
            disabled={busy || definitionKey === '' || (limited && scope === ALL_BRANDS)}
          >
            <Icon name="plus" />
            Create schedule
          </Button>
        </div>
      ) : null}
    </Dialog>
  );
}

/** Tick or untick one key, refusing to go past the cap the server enforces. */
function toggle(list: string[], set: (v: string[]) => void, key: string, max: number): void {
  if (list.includes(key)) set(list.filter((k) => k !== key));
  else if (list.length < max) set([...list, key]);
}

/* ── The ad-hoc builder ─────────────────────────────────────────────────────
   Every option comes from /api/reports/sources, which is served from the same
   map the runner validates against. The picker therefore cannot offer a key the
   server would refuse, and editing the page cannot invent one that works. */
function BuilderDialog({
  open,
  onClose,
  window,
  brandId,
  onRan,
}: {
  open: boolean;
  onClose: () => void;
  window: { from: string; to: string };
  brandId: string | null;
  onRan: (run: RunResult) => void;
}) {
  const { showError } = useErrorDialog();
  const [meta, setMeta] = useState<SourcesResponse | null>(null);
  const [sourceKey, setSourceKey] = useState('');
  const [dimensions, setDimensions] = useState<string[]>([]);
  const [measures, setMeasures] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || meta) return;
    api
      .get<SourcesResponse>('/api/reports/sources')
      .then((d) => {
        setMeta(d);
        const first = d.sources[0];
        if (first) {
          setSourceKey(first.key);
          setDimensions(first.dimensions[0] ? [first.dimensions[0].key] : []);
          setMeasures(first.measures[0] ? [first.measures[0].key] : []);
        }
      })
      .catch((error: unknown) => {
        showError({
          title: 'Could not load the builder',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The list of things a report may ask for could not be read.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      });
  }, [open, meta, showError]);

  const source = meta?.sources.find((s) => s.key === sourceKey) ?? null;

  const pickSource = (key: string) => {
    setSourceKey(key);
    // Dimensions and measures belong to a source. Carrying the previous
    // selection across would send keys the new source does not have, and the
    // server would refuse the run with a message about a dimension the operator
    // never knowingly chose.
    const next = meta?.sources.find((s) => s.key === key);
    setDimensions(next?.dimensions[0] ? [next.dimensions[0].key] : []);
    setMeasures(next?.measures[0] ? [next.measures[0].key] : []);
  };

  const run = () => {
    setBusy(true);
    api
      .post<RunResult>('/api/reports/run', {
        source: sourceKey,
        dimensions,
        measures,
        ...window,
        ...(brandId ? { brandId } : {}),
      })
      .then(onRan)
      .catch((error: unknown) => {
        showError({
          title: 'Could not run that report',
          message:
            error instanceof ApiRequestError ? error.message : 'The report did not run.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => setBusy(false));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      wide
      title="Build a report"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={run} disabled={busy || measures.length === 0}>
            {busy ? 'Running…' : 'Run report'}
          </Button>
        </>
      }
    >
      {!meta ? (
        <div className="skeleton" style={{ height: '200px' }}>&nbsp;</div>
      ) : (
        <>
          <Field label="Dataset">
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={sourceKey}
                onChange={(e) => pickSource(e.target.value)}
              >
                {meta.sources.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label={`Group by · up to ${meta.maxDimensions}`}
            hint="Leave all unticked for a single total row."
          >
            {() => (
            <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--space-2) var(--space-6)' }}>
              {source?.dimensions.map((d) => (
                <Choice
                  key={d.key}
                  type="checkbox"
                  label={d.label}
                  checked={dimensions.includes(d.key)}
                  disabled={!dimensions.includes(d.key) && dimensions.length >= meta.maxDimensions}
                  onChange={() => toggle(dimensions, setDimensions, d.key, meta.maxDimensions)}
                />
              ))}
            </div>
            )}
          </Field>

          <Field
            label={`Measure · up to ${meta.maxMeasures}`}
            {...(measures.length === 0
              ? { error: 'Choose at least one, or every row is a label with no number.' }
              : {})}
          >
            {() => (
            <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--space-2) var(--space-6)' }}>
              {source?.measures.map((m) => (
                <Choice
                  key={m.key}
                  type="checkbox"
                  label={m.label}
                  checked={measures.includes(m.key)}
                  disabled={!measures.includes(m.key) && measures.length >= meta.maxMeasures}
                  onChange={() => toggle(measures, setMeasures, m.key, meta.maxMeasures)}
                />
              ))}
            </div>
            )}
          </Field>

          <div className="text-subtle" style={{ fontSize: '12px' }}>
            {builderWindowLine(window, source?.snapshot ?? false, meta.rowCap)}
          </div>
        </>
      )}
    </Dialog>
  );
}
